import React, { useState } from "react";
import { ArrowLeft, ChevronRight, ChevronLeft, CalendarRange, Building2, Search, AlertTriangle } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtEur } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import PuliziaFantasmaButton from "./PuliziaFantasmaButton";
import BusteArchivioButton from "./BusteArchivioButton";
import BusteDocumenti from "./BusteDocumenti";

const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const orderStructName = (name) => { const n = (name || "").toLowerCase(); if (n.includes("villa")) return 0; if (n.includes("boschetto")) return 1; if (n.includes("dependance") || n.includes("dépendance")) return 2; return 3; };
const MAT_BG = "bg-amber-50/70";
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

export default function BustePagaCosti() {
  const { selectedStructure, can, structures = [] } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [month, setMonth] = useState(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [contrFilter, setContrFilter] = useState("tutti");
  const [search, setSearch] = useState("");
  const readOnly = !can("buste_paga");

  const structMap = React.useMemo(() => Object.fromEntries((structures || []).map((s) => [s.id, s.nome])), [structures]);
  const structName = (id) => structMap[id] || "—";

  const { data = [] } = useQuery({
    queryKey: ["buste_paga", selectedStructure],
    queryFn: async () => (await api.get("/buste_paga", { params: { structure_id: selectedStructure } })).data,
  });
  const { data: opsList = [] } = useQuery({
    queryKey: ["firma-ops-all", selectedStructure],
    queryFn: async () => (await api.get("/firma/operatori", { params: { structure_id: selectedStructure } })).data,
  });
  const contrByKey = React.useMemo(() => {
    const m = {};
    opsList.forEach((o) => { const c = (o.contratto || "").toLowerCase(); m[o.id] = c; m[`${o.nome} ${o.cognome}`.trim()] = c; });
    return m;
  }, [opsList]);
  const isCoop = (r) => /cooperativ/.test(contrByKey[r.operatore_id] ?? contrByKey[(r.operatore_nome || "").trim()] ?? "");
  const { data: riepilogo = [] } = useQuery({
    queryKey: ["buste_paga_riepilogo", year, selectedStructure],
    queryFn: async () => (await api.get("/buste_paga/riepilogo", { params: { anno: year, structure_id: selectedStructure } })).data,
  });
  const { data: roster = [] } = useQuery({
    queryKey: ["buste_paga_mese", month, selectedStructure],
    queryFn: async () => (await api.get(`/buste_paga/mese/${month}`, { params: { structure_id: selectedStructure } })).data,
    enabled: !!month,
  });
  const { data: f24mYear = [] } = useQuery({
    queryKey: ["f24_mensili", year, selectedStructure],
    queryFn: async () => (await api.get("/tfr_f24/mensili", { params: { anno: year, structure_id: selectedStructure } })).data,
    enabled: !!month,
  });
  const f24mRow = f24mYear.find((r) => r.mese === month);
  const f24Scost = (f24mRow && f24mRow.pagato && f24mRow.f24_personale_override != null)
    ? (f24mRow.f24_personale_override - (f24mRow.f24_personale_auto || 0)) : 0;
  const rows = roster
    .filter((r) => contrFilter === "tutti" || (contrFilter === "coop" ? isCoop(r) : !isCoop(r)))
    .filter((r) => { const q = search.trim().toLowerCase(); return !q || (r.operatore_nome || "").toLowerCase().includes(q); })
    .sort((a, b) =>
      (structName(a.structure_id)).localeCompare(structName(b.structure_id), "it") ||
      (a.operatore_nome || "").localeCompare(b.operatore_nome || "", "it"));

  const save = async (row, patch) => {
    try {
      if (row.operatore_id) {
        await api.post("/buste_paga/upsert", { mese: month, operatore_id: row.operatore_id, structure_id: row.structure_id, operatore_nome: row.operatore_nome, ...patch });
      } else if (row.id) {
        const m = { ...row, ...patch }; const lordo = num(m.netto) + num(m.inps) + num(m.irpef);
        await api.put(`/buste_paga/${row.id}`, { ...patch, lordo, totale_costo: lordo + num(m.tfr) + num(m.inail) });
      }
      qc.invalidateQueries({ queryKey: ["buste_paga_mese"] });
      qc.invalidateQueries({ queryKey: ["buste_paga_riepilogo"] });
      qc.invalidateQueries({ queryKey: ["buste_paga"] });
    } catch { toast.error("Errore salvataggio"); }
  };

  const Cell = ({ row, field }) => { const rk = row.operatore_id || row.id; return (
    <input type="number" step="0.01" disabled={readOnly} defaultValue={row[field] ?? 0} key={`${rk}-${field}-${row[field]}`}
      data-testid={`bp-${field}-${rk}`}
      onBlur={(e) => { if (num(e.target.value) !== num(row[field])) save(row, { [field]: num(e.target.value) }); }}
      className="w-20 rounded border px-2 py-1 text-sm text-right disabled:opacity-60" />
  ); };

  const sum = (f) => rows.reduce((a, r) => a + num(r[f]), 0);
  const lordoTot = rows.reduce((a, r) => a + num(r.netto) + num(r.inps) + num(r.irpef), 0);
  const f24Costi = sum("inps") + sum("irpef");
  const f24Mat = sum("inail");
  const tfrTot = sum("tfr");
  const matTot = tfrTot + sum("inail");
  const bustaTot = lordoTot + matTot;
  const benefitTot = sum("benefit");
  const conBenefitTot = bustaTot + benefitTot;

  if (!month) {
    const monthsData = MESI.map((lbl, i) => {
      const key = `${year}-${String(i + 1).padStart(2, "0")}`;
      const r = riepilogo.find((x) => x.mese === key);
      return { key, lbl, count: r ? r.count : 0, tot: r ? r.totale : 0 };
    });
    return (
      <div>
        <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
          <div><h3 className="text-lg font-head font-bold">Buste paga per mese</h3><p className="text-sm text-muted-foreground">Seleziona un mese per vedere costi e maturazioni</p></div>
          <div className="flex items-center gap-2"><span className="text-sm font-semibold text-muted-foreground">Anno:</span>
            <select value={year} onChange={(e) => setYear(e.target.value)} className="h-9 rounded-md border px-2 text-sm font-semibold" data-testid="bp-year">
              {[0, 1, 2, 3].map((o) => { const y = String(new Date().getFullYear() - o); return <option key={y} value={y}>{y}</option>; })}
            </select>
            {!readOnly && <PuliziaFantasmaButton />}
            {!readOnly && <BusteArchivioButton />}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {monthsData.map((mo) => (
            <button key={mo.key} onClick={() => setMonth(mo.key)} data-testid={`bp-month-${mo.key}`}
              className={`gr-card p-4 text-left flex items-center justify-between hover:border-primary ${mo.count === 0 ? "opacity-70" : ""}`}>
              <div>
                <div className="font-head font-bold">{mo.lbl}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{mo.count} operatori</div>
                <div className="text-lg font-head font-bold text-primary mt-1">{fmtEur(mo.tot)}</div>
              </div>
              <ChevronRight className="text-muted-foreground" size={18} />
            </button>
          ))}
        </div>
      </div>
    );
  }

  const Th = ({ children, mat, right }) => <th className={`py-2 px-2 ${right ? "text-right" : ""} ${mat ? MAT_BG : ""}`}>{children}</th>;

  const monthLabel = (k) => { if (!k) return ""; const [y, m] = k.split("-").map(Number); return `${MESI[m - 1]} ${y}`; };
  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    const ny = d.getFullYear();
    setYear(String(ny));
    setMonth(`${ny}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap flex-1 min-w-0">
          <Button variant="ghost" onClick={() => setMonth(null)} data-testid="bp-month-back"><ArrowLeft size={16} className="mr-1" /> Tutti i mesi</Button>
          <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shiftMonth(-1)} data-testid="bp-month-prev" title="Mese precedente"><ChevronLeft size={18} /></Button>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white shadow-sm" data-testid="bp-month-badge">
            <CalendarRange size={18} />
            <span className="font-head font-bold text-lg capitalize">{monthLabel(month)}</span>
          </div>
          <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shiftMonth(1)} data-testid="bp-month-next" title="Mese successivo"><ChevronRight size={18} /></Button>
          <span className="text-xs text-muted-foreground ml-auto" data-testid="bp-count">{rows.length} operatori</span>
        </div>
        <BusteDocumenti meseFisso={month} />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-lg border overflow-hidden" data-testid="bp-contr-filter">
          {[["tutti", "Tutti"], ["coop", "Cooperativa"], ["interni", "Interni"]].map(([k, lbl]) => (
            <button key={k} type="button" onClick={() => setContrFilter(k)} data-testid={`bp-filter-${k}`}
              className={`h-9 px-4 text-sm font-semibold transition-colors ${contrFilter === k ? "bg-primary text-white" : "bg-white hover:bg-secondary/60 text-muted-foreground"}`}>
              {lbl}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome o cognome…" data-testid="bp-search" className="h-9 pl-8 w-64" />
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="gr-card p-10 text-center text-muted-foreground">Nessun operatore attivo per questo mese/filtro.</div>
      ) : (
        <>
          <div className="gr-card p-5 overflow-x-auto">
            <table className="w-full text-sm border-collapse whitespace-nowrap">
              <thead>
                <tr className="text-xs uppercase tracking-wide">
                  <th></th>
                  <th className="py-1 px-2 text-center text-primary font-bold border-b-2 border-primary/40" colSpan={5}>Costi mensili</th>
                  <th className={`py-1 px-2 text-center text-amber-700 font-bold border-b-2 border-amber-400 ${MAT_BG}`} colSpan={4}>Maturazione costi</th>
                  <th className="py-1 px-2" colSpan={3}></th>
                </tr>
                <tr className="text-left text-muted-foreground border-b">
                  <Th>Operatore</Th>
                  <Th>Netto</Th><Th>INPS</Th><Th>IRPEF</Th><Th right>F24 (INPS+IRPEF)</Th><Th right>Tot. Lordo</Th>
                  <Th mat>TFR</Th><Th mat>INAIL</Th><Th mat right>F24 (INAIL)</Th><Th mat right>Tot. Mat.</Th>
                  <Th right>Tot. busta paga</Th><Th>Benefit</Th><Th right>Tot. + benefit</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const lordo = num(r.netto) + num(r.inps) + num(r.irpef);
                  const mat = num(r.tfr) + num(r.inail);
                  const busta = lordo + mat;
                  const rk = r.operatore_id || r.id;
                  return (
                    <tr key={rk} className="border-b last:border-0" data-testid={`bp-row-${rk}`}>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          <span className="inline-grid place-items-center min-w-[22px] h-[22px] px-1 rounded bg-secondary text-[11px] font-bold text-muted-foreground tabnum shrink-0" data-testid={`bp-num-${rk}`}>{idx + 1}</span>
                          <div>
                            {r.operatore_id
                              ? <button type="button" onClick={() => navigate(`/operatori/${r.operatore_id}`)} data-testid={`bp-nome-${rk}`} className="font-semibold text-primary hover:underline text-left" title="Apri la scheda dell'operatore">{r.operatore_nome}</button>
                              : <div className="font-semibold">{r.operatore_nome}</div>}
                            <div className="text-xs text-muted-foreground">{structName(r.structure_id)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-1 px-2"><Cell row={r} field="netto" /></td>
                      <td className="py-1 px-2"><Cell row={r} field="inps" /></td>
                      <td className="py-1 px-2"><Cell row={r} field="irpef" /></td>
                      <td className="py-1 px-2 text-right text-red-600 font-semibold">{fmtEur(num(r.inps) + num(r.irpef))}</td>
                      <td className="py-1 px-2 text-right font-semibold">{fmtEur(lordo)}</td>
                      <td className={`py-1 px-2 ${MAT_BG}`}><Cell row={r} field="tfr" /></td>
                      <td className={`py-1 px-2 ${MAT_BG}`}><Cell row={r} field="inail" /></td>
                      <td className={`py-1 px-2 text-right text-red-600 font-semibold ${MAT_BG}`}>{fmtEur(num(r.inail))}</td>
                      <td className={`py-1 px-2 text-right font-semibold ${MAT_BG}`}>{fmtEur(mat)}</td>
                      <td className="py-1 px-2 text-right font-bold">{fmtEur(busta)}</td>
                      <td className="py-1 px-2"><Cell row={r} field="benefit" /></td>
                      <td className="py-1 px-2 text-right font-bold text-primary">{fmtEur(busta + num(r.benefit))}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 font-bold">
                  <td className="py-2 pr-3">Totali</td>
                  <td className="py-2 px-2">{fmtEur(sum("netto"))}</td><td className="py-2 px-2">{fmtEur(sum("inps"))}</td>
                  <td className="py-2 px-2">{fmtEur(sum("irpef"))}</td><td className="py-2 px-2 text-right text-red-600">{fmtEur(f24Costi)}</td>
                  <td className="py-2 px-2 text-right">{fmtEur(lordoTot)}</td>
                  <td className={`py-2 px-2 ${MAT_BG}`}>{fmtEur(tfrTot)}</td><td className={`py-2 px-2 ${MAT_BG}`}>{fmtEur(sum("inail"))}</td>
                  <td className={`py-2 px-2 text-right text-red-600 ${MAT_BG}`}>{fmtEur(f24Mat)}</td><td className={`py-2 px-2 text-right ${MAT_BG}`}>{fmtEur(matTot)}</td>
                  <td className="py-2 px-2 text-right">{fmtEur(bustaTot)}</td><td className="py-2 px-2 text-right">{fmtEur(benefitTot)}</td>
                  <td className="py-2 px-2 text-right text-primary">{fmtEur(conBenefitTot)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {selectedStructure === "all" && (() => {
            const sids = [...new Set(rows.map((r) => r.structure_id || ""))];
            if (sids.length <= 1) return null;
            return <div className="font-head font-bold text-sm text-primary uppercase tracking-wide">Riepilogo finale — tutte le strutture</div>;
          })()}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="gr-card p-5 border-l-4" style={{ borderColor: "#dc2626" }} data-testid="bp-f24">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">F24 da pagare subito</div>
              <div className="text-2xl font-head font-bold text-red-600 mt-1">{fmtEur(f24Costi)}</div>
              <div className="text-[11px] text-muted-foreground mt-1">Solo INPS+IRPEF (costi mensili)</div>
              {Math.abs(f24Scost) > 10 && (
                <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 p-2" data-testid="bp-f24-scostamento">
                  <div className="flex items-center gap-1.5 text-[11px] font-bold text-amber-800"><AlertTriangle size={12} /> Versato diverso dal preventivato</div>
                  <div className="text-[11px] text-amber-700 mt-0.5">Versato {fmtEur(f24mRow.f24_personale_override)} · scostamento {f24Scost > 0 ? "+" : "−"}{fmtEur(Math.abs(f24Scost))}</div>
                  {f24mRow.f24_personale_motivo && <div className="text-[10px] text-muted-foreground mt-0.5">Motivo: {f24mRow.f24_personale_motivo}</div>}
                </div>
              )}
            </div>
            <div className="gr-card p-5 border-l-4" style={{ borderColor: "#c2660c" }} data-testid="bp-tfr">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">TFR da accantonare</div>
              <div className="text-2xl font-head font-bold text-amber-700 mt-1">{fmtEur(tfrTot)}</div>
            </div>
            <div className="gr-card p-5 border-l-4" style={{ borderColor: "#7c3aed" }} data-testid="bp-f24-inail">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">F24 costi che maturano</div>
              <div className="text-2xl font-head font-bold text-violet-700 mt-1">{fmtEur(f24Mat)}</div>
              <div className="text-[11px] text-muted-foreground mt-1">INAIL sui costi che maturano</div>
            </div>
            <div className="gr-card p-5 border-l-4" style={{ borderColor: "#1f7a5c" }} data-testid="bp-tot-generale">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Totale con benefit</div>
              <div className="text-2xl font-head font-bold text-primary mt-1">{fmtEur(conBenefitTot)}</div>
              <div className="text-[11px] text-muted-foreground mt-1">Busta {fmtEur(bustaTot)} + benefit {fmtEur(benefitTot)}</div>
            </div>
          </div>

          {selectedStructure === "all" && (() => {
            const bySid = {};
            rows.forEach((r) => { const k = r.structure_id || ""; (bySid[k] = bySid[k] || []).push(r); });
            const sids = Object.keys(bySid).sort((a, b) => orderStructName(structName(a)) - orderStructName(structName(b)) || structName(a).localeCompare(structName(b), "it"));
            if (sids.length <= 1) return null;
            const S = (rs, f) => rs.reduce((a, r) => a + num(r[f]), 0);
            return (
              <div className="space-y-4 mt-6 pt-4 border-t-2" data-testid="bp-dettaglio-strutture">
                <div className="font-head font-bold text-sm text-muted-foreground uppercase tracking-wide">Dettaglio per struttura</div>
                {sids.map((sid) => {
                  const rs = bySid[sid];
                  const f24c = S(rs, "inps") + S(rs, "irpef"); const inail = S(rs, "inail"); const tfr = S(rs, "tfr"); const benefit = S(rs, "benefit");
                  return (
                    <div key={sid} data-testid={`bp-struct-${sid}`}>
                      <div className="font-head font-bold mb-2 flex items-center gap-2"><Building2 size={15} className="text-primary" /> {structName(sid)}</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="gr-card p-4 border-l-4" style={{ borderColor: "#dc2626" }}><div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">F24 da pagare subito</div><div className="text-xl font-head font-bold text-red-600 mt-1">{fmtEur(f24c)}</div></div>
                        <div className="gr-card p-4 border-l-4" style={{ borderColor: "#c2660c" }}><div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">TFR da accantonare</div><div className="text-xl font-head font-bold text-amber-700 mt-1">{fmtEur(tfr)}</div></div>
                        <div className="gr-card p-4 border-l-4" style={{ borderColor: "#7c3aed" }}><div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">F24 costi che maturano</div><div className="text-xl font-head font-bold text-violet-700 mt-1">{fmtEur(inail)}</div></div>
                        <div className="gr-card p-4 border-l-4" style={{ borderColor: "#1f7a5c" }}><div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Totale benefit</div><div className="text-xl font-head font-bold text-primary mt-1">{fmtEur(benefit)}</div></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
